import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * How a payment here can be taken (owner 2026-11-01, radaccess
 * GET /access/operator/{ns}/pay-options). A screen shows this and never names a
 * provider, so adding one changes no screen.
 */
export interface PayOptions {
  online: { available: boolean; mode?: string; reason?: string };
  upi?: { upi_id: string; label: string };
}

/** The owner's exact words: shown under a greyed Pay online. */
export const NO_GATEWAY_NOTICE = "Payment Gateway not Configured";

/** A gateway is live, but Pay online is not switched on for this console yet. */
export const PAY_ONLINE_OFF_NOTICE = "Pay online is not switched on yet.";

export async function getPayOptions(identity: CallerIdentity, ns: string, accountId: string, signal?: AbortSignal): Promise<PayOptions> {
  const q = accountId ? `?account=${encodeURIComponent(accountId)}` : "";
  const r = await callGateway<PayOptions>(identity, config().gatewayBase, `/operator/${encodeURIComponent(ns)}/pay-options${q}`, signal ? { signal } : {});
  return r.data ?? { online: { available: false } };
}

/**
 * The UPI payment address a QR holds, with the amount filled in. A UPI ID stays
 * readable ("@" unescaped), since some UPI apps reject %40.
 */
export function upiPayURL(upiId: string, payee: string, amount: number | null, note: string): string {
  const parts = [`pa=${encodeURIComponent(upiId).replace(/%40/g, "@")}`, `pn=${encodeURIComponent(payee)}`];
  if (amount != null && amount > 0) parts.push(`am=${amount.toFixed(2)}`);
  parts.push("cu=INR");
  if (note) parts.push(`tn=${encodeURIComponent(note)}`);
  return `upi://pay?${parts.join("&")}`;
}

/** The line under Pay online; "" when none is needed. A failed read is never "not configured". */
export function payOnlineLine(options: PayOptions | undefined, readFailed: boolean, why: string): string {
  if (readFailed) return "Could not check the payment gateway; try again.";
  if (!options) return "";
  return options.online.available ? why : NO_GATEWAY_NOTICE;
}
